import { useToast } from '@get-mosh/msh-shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { trackGateTriggered } from 'src/analytics/Events';
import ApiRoutes from 'src/constants/ApiRoutes';
import PortalRoutes from 'src/constants/PortalRoutes';
import Routes from 'src/constants/Routes';
import { Vertical } from 'src/constants/Verticals';
import {
    checkIdentifyFromQuizResponse,
    checkLoginFromQuizResponse,
} from 'src/helpers/quiz';
import { camelizeObjectKeys } from 'src/helpers/utils';
import { getHeaders } from 'src/services/api';
import { ApiError } from 'src/types/Api';
import { QUIZ_FLOW_TYPES, QuizFlow, QuizRecord } from 'src/types/Quiz';

export type SubmitAnswerParams = {
    questionId: string;
    questionText: string;
    answerIds?: string[];
    answerTexts?: string[];
    medication?: string;
    uploadFile?: File | null;
    lastModifiedDate?: Date | null;
    callback?: (response: QuizRecord) => void;
    options?: { signal?: AbortSignal };
};

async function submitAnswer(
    quizToken: string,
    quizFlow: string,
    {
        questionId,
        questionText,
        answerIds = [],
        answerTexts = [],
        medication = '',
        uploadFile = null,
        lastModifiedDate = null,
        options = {},
    }: Omit<SubmitAnswerParams, 'callback'>,
) {
    const formData = new FormData();
    formData.append('quiz_token', quizToken);
    formData.append('question_frontend_stamp', questionId);
    formData.append('question_text', questionText);

    answerIds.forEach((answerId) =>
        formData.append('answer_frontend_stamps[]', answerId),
    );
    answerTexts.forEach((answerText) =>
        formData.append('answer_texts[]', answerText),
    );
    formData.append('medication', medication);
    if (uploadFile) {
        formData.append('file_upload', uploadFile);
    }
    if (lastModifiedDate) {
        formData.append('last_modified_date', lastModifiedDate.toISOString());
    }
    const headers = getHeaders();
    // Remove content type header to allow browser to set it automatically
    headers.delete('Content-Type');

    // Request
    const routeByType = {
        [QUIZ_FLOW_TYPES.initial]: ApiRoutes.INITIAL_QUIZZES_SUBMIT_ANSWER,
        [QUIZ_FLOW_TYPES.renewal]: ApiRoutes.RENEWAL_QUIZZES_SUBMIT_ANSWER,
        [QUIZ_FLOW_TYPES.symptoms]:
            ApiRoutes.HEALTH_TRACKING_QUIZZES_SUBMIT_ANSWER,
    };
    const route = routeByType[quizFlow];
    const res = await fetch(route, {
        method: 'POST',
        headers,
        body: formData,
        ...options,
    });

    const data = await res.json();

    if (!res.ok) {
        throw data as ApiError;
    }

    const questionRecord = camelizeObjectKeys(data) as QuizRecord;

    return questionRecord;
}

type SubmitAnswerConfig = {
    quizFlow: QuizFlow;
    quizToken: string;
    quizType: Vertical;
    quizVersion: string;
    submittedQuestionIds: string[];
    setSubmittedQuestionIds: (ids: string[]) => void;
};

export function useSubmitAnswer({
    quizFlow,
    quizToken,
    quizType,
    quizVersion,
    submittedQuestionIds,
    setSubmittedQuestionIds,
}: SubmitAnswerConfig) {
    const queryClient = useQueryClient();
    const router = useRouter();
    const { showToast } = useToast();

    return useMutation({
        mutationKey: ['SubmitAnswer', quizFlow, quizToken, quizType],
        mutationFn: (params: SubmitAnswerParams) =>
            submitAnswer(quizToken, quizFlow, params),
        onSuccess: (questionRecord, { callback, questionId }) => {
            checkLoginFromQuizResponse(questionRecord);
            checkIdentifyFromQuizResponse(questionRecord);
            setSubmittedQuestionIds([...submittedQuestionIds, questionId]);
            if (typeof callback === 'function') callback(questionRecord);
            queryClient.setQueryData(['Quiz', quizToken], questionRecord);
        },
        onError: (data, { answerTexts = [] }) => {
            const error = data as unknown as ApiError;
            const errorParams = {
                error_type: error.type,
                error_message: error.message,
                error_title: error.title || 'There was an error',
                ...(error.type === 'quiz_already_completed' && {
                    cta_text: 'Go to account',
                    cta_url: PortalRoutes.NOTIFICATIONS,
                }),
            };

            if (error.type === 'phone_number') {
                trackGateTriggered(Vertical[quizType.toUpperCase()], {
                    quiz_token: quizToken,
                    quiz_name: `${quizType} - ${quizVersion}`,
                    label: 'Gate Triggered',
                    gate_type: error.type,
                    gate_source: 'Quiz',
                });
                router.push(
                    Routes.VERIFY_PHONE(answerTexts[0], quizType, quizToken),
                );
            } else if (error.type === 'email') {
                trackGateTriggered(Vertical[quizType.toUpperCase()], {
                    quiz_token: quizToken,
                    quiz_name: `${quizType} - ${quizVersion}`,
                    label: 'Gate Triggered',
                    gate_type: error.type,
                    gate_source: 'Quiz',
                });
                router.push(Routes.VERIFY_EMAIL(answerTexts[0]));
            } else if (error.type === 'age') {
                trackGateTriggered(Vertical[quizType.toUpperCase()], {
                    quiz_token: quizToken,
                    quiz_name: `${quizType} - ${quizVersion}`,
                    label: 'Gate Triggered',
                    gate_type: error.type,
                    gate_source: 'Quiz',
                });
                window.location.href = Routes.ERROR(errorParams);
            } else if (error.type === 'invalid_request') {
                if (error.errors?.[0] && error.errors[0].key === 'fileUpload') {
                    showToast({
                        variant: 'error',
                        title: error.errors[0].message,
                    });
                }
            } else {
                window.location.href = Routes.ERROR(errorParams);
            }
        },
    });
}
